/**
 * Permission keys and per-resource capabilities.
 *
 * IMPORTANT: these are *lookup keys only*. Which role holds which permission is decided
 * by the backend (role_permissions in PostgreSQL) and delivered in GET /api/users/me.
 * Nothing here grants anything, and none of it is a security control — every check is
 * repeated server-side. See docs/05-security-architecture.md §1.3.
 */

export const P = {
  // documents
  DOCUMENT_VIEW: 'DOCUMENT_VIEW',
  DOCUMENT_UPLOAD: 'DOCUMENT_UPLOAD',
  DOCUMENT_DOWNLOAD: 'DOCUMENT_DOWNLOAD',
  DOCUMENT_EDIT: 'DOCUMENT_EDIT',
  DOCUMENT_REPLACE: 'DOCUMENT_REPLACE',
  DOCUMENT_DELETE: 'DOCUMENT_DELETE',
  DOCUMENT_DELETE_PERMANENT: 'DOCUMENT_DELETE_PERMANENT',
  DOCUMENT_COMMENT: 'DOCUMENT_COMMENT',
  // folders
  FOLDER_CREATE: 'FOLDER_CREATE',
  FOLDER_RENAME: 'FOLDER_RENAME',
  FOLDER_DELETE: 'FOLDER_DELETE',
  FOLDER_MOVE: 'FOLDER_MOVE',
  // sharing
  DOCUMENT_SHARE: 'DOCUMENT_SHARE',
  DOCUMENT_SHARE_EXTERNAL: 'DOCUMENT_SHARE_EXTERNAL',
  DOCUMENT_SIGN_REQUEST: 'DOCUMENT_SIGN_REQUEST',
  // search
  SEARCH_AI: 'SEARCH_AI',
  // administration
  ADMIN_USER_MANAGE: 'ADMIN_USER_MANAGE',
  ADMIN_ROLE_MANAGE: 'ADMIN_ROLE_MANAGE',
  ADMIN_DEPARTMENT_MANAGE: 'ADMIN_DEPARTMENT_MANAGE',
  ADMIN_AUDIT_VIEW: 'ADMIN_AUDIT_VIEW',
  ADMIN_STORAGE_VIEW: 'ADMIN_STORAGE_VIEW',
  ADMIN_SETTINGS_MANAGE: 'ADMIN_SETTINGS_MANAGE',
  ADMIN_DOCUMENT_OVERRIDE: 'ADMIN_DOCUMENT_OVERRIDE',
};

/** Capabilities on a single document or folder, returned per-resource by the API. */
export const CAP = {
  VIEW: 'view',
  DOWNLOAD: 'download',
  EDIT: 'edit',
  REPLACE: 'replace',
  SHARE: 'share',
  DELETE: 'delete',
  MANAGE: 'manage',
};

export const CAPABILITY_LABELS = {
  view: 'Can view',
  download: 'Can view + download',
  edit: 'Can edit metadata',
  replace: 'Can replace version',
  share: 'Can share',
  delete: 'Can delete',
  manage: 'Full access',
};

/** The share dialog offers capability bundles, in increasing order of privilege. */
export const SHARE_LEVELS = [
  { key: 'view', label: 'Can view', caps: ['view'], hint: 'Preview only — no download' },
  { key: 'download', label: 'Can view + download', caps: ['view', 'download'], hint: 'Preview and download a copy' },
  { key: 'edit', label: 'Can edit metadata', caps: ['view', 'download', 'edit'], hint: 'Rename, tag and describe' },
  {
    key: 'replace',
    label: 'Can replace version',
    caps: ['view', 'download', 'edit', 'replace'],
    hint: 'Upload new versions of this document',
  },
  {
    key: 'share',
    label: 'Can share',
    caps: ['view', 'download', 'edit', 'replace', 'share'],
    hint: 'Everything above, plus re-share with others',
  },
];

/** Short pill label for a capability set: "View" | "Download" | "Edit" (v2 share pills). */
export function shortLevel(caps = []) {
  const set = new Set(caps);
  if (set.has('replace') || set.has('edit')) return 'Edit';
  if (set.has('download')) return 'Download';
  return 'View';
}

/** Map a capability array back to the closest named level, for display. */
export function capsToLevel(caps = []) {
  const set = new Set(caps);
  for (let i = SHARE_LEVELS.length - 1; i >= 0; i -= 1) {
    if (SHARE_LEVELS[i].caps.every((c) => set.has(c))) return SHARE_LEVELS[i];
  }
  return SHARE_LEVELS[0];
}

/**
 * A user may never grant a capability they do not hold themselves.
 * Mirrors the server-side intersection in docs/05-security-architecture.md §1.1.
 */
export function grantableLevels(ownCapabilities = []) {
  const own = new Set(ownCapabilities);
  if (!own.has('share')) return [];
  return SHARE_LEVELS.filter((level) => level.caps.every((c) => own.has(c)));
}

export function hasCapability(resource, capability) {
  return Array.isArray(resource?.capabilities) && resource.capabilities.includes(capability);
}

/** Permission catalogue metadata, used to group the role matrix editor. */
export const PERMISSION_CATEGORIES = [
  { key: 'documents', label: 'Documents' },
  { key: 'folders', label: 'Folders' },
  { key: 'sharing', label: 'Sharing' },
  { key: 'search', label: 'Search' },
  { key: 'administration', label: 'Administration' },
];
